import { Link } from "react-router-dom";
import { ArrowRight, Scissors, Sparkles, Ruler } from "lucide-react";
import { RcButton } from "@/components/RcButton";
import { useSiteContent } from "@/lib/siteContent";
import { useSeo } from "@/lib/useSeo";

const showcase = [
  { url: "https://images.unsplash.com/photo-1654764746225-e63f5e90facd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBicmlkYWwlMjBsZWhlbmdhJTIwZW1icm9pZGVyeXxlbnwwfHx8fDE3ODI0NDMxNzB8MA&ixlib=rb-4.1.0&q=85", span: "md:col-span-7 md:row-span-2", label: "Bridal Lehenga" },
  { url: "https://images.unsplash.com/photo-1619043518800-7f14be467dca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxzaWxrJTIwZmFicmljJTIwdGV4dHVyZSUyMHJpY2h8ZW58MHx8fHwxNzgyNDQzMTcwfDA&ixlib=rb-4.1.0&q=85", span: "md:col-span-5", label: "Pure Silk" },
  { url: "https://images.pexels.com/photos/135620/pexels-photo-135620.jpeg", span: "md:col-span-5", label: "The Atelier" },
];

export default function Landing() {
  const site = useSiteContent();
  useSeo({
    title: "Park Collections — Bespoke Indian Boutique | Design Your Own Lehenga, Kurti, Gown",
    description: "Design your own custom Indian & Western outfit at Park Collections. Choose fabric, neckline, embroidery, sleeves and measurements. AI preview before you order. Bespoke tailoring delivered pan-India.",
    path: "/",
  });
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-[88vh] grain overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/8752281/pexels-photo-8752281.jpeg"
            alt="Rich red fabric backdrop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#120D0C] via-[#120D0C]/70 to-[#120D0C]/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20 grid md:grid-cols-12 gap-10 items-center min-h-[88vh]">
          <div className="md:col-span-8 rc-enter">
            <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.3em] uppercase text-[#CFA05E]">
              <span className="h-px w-10 bg-[#CFA05E]" /> A Bespoke Atelier
            </div>
            <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-4xl" data-testid="hero-headline">
              {site.hero_headline}
            </h1>
            <p className="mt-8 text-base sm:text-lg text-[#A39E93] max-w-xl leading-relaxed font-light" data-testid="hero-subhead">
              {site.hero_subhead}
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/designer" data-testid="hero-start-design">
                <RcButton variant="primary" data-testid="hero-start-design-btn">
                  Start Designing <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </RcButton>
              </Link>
              <Link to="/auth" data-testid="hero-signin">
                <RcButton variant="outline">Sign In</RcButton>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-4">The Process</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-tight">
              Three quiet steps to a garment that is wholly yours.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-8">
            {[
              { icon: Scissors, n: "01", t: "Choose silhouette", d: "From kurti to gown — select your dress type, length, and neckline." },
              { icon: Sparkles, n: "02", t: "Curate the detail", d: "Hand-pick fabric, color, embroidery and sleeves with live pricing." },
              { icon: Ruler, n: "03", t: "Preview & order", d: "AI-rendered preview, secure checkout, tailored to your measurements." },
            ].map(({ icon: Icon, n, t, d }) => (
              <div key={n} className="border-t border-[#362B28] pt-6 rc-enter">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-[#A39E93] tracking-[0.3em]">{n}</span>
                  <Icon className="w-5 h-5 text-[#CFA05E]" strokeWidth={1.4} />
                </div>
                <div className="font-display text-2xl mb-3">{t}</div>
                <p className="text-sm text-[#A39E93] leading-relaxed font-light">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="grid md:grid-cols-12 gap-4 md:gap-6 md:auto-rows-[260px]">
          {showcase.map((s) => (
            <div key={s.label} className={`relative overflow-hidden group ${s.span}`}>
              <img src={s.url} alt={s.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#120D0C]/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 text-xs tracking-[0.3em] uppercase text-[#FDFBF7]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SEO CONTENT SECTIONS */}
      <section className="border-t border-[#362B28]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-4">The Atelier</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-tight">
              Bespoke Indian &amp; Western couture, tailored to you.
            </h2>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-8 text-sm leading-relaxed text-[#A39E93] font-light">
            <div>
              <h3 className="font-display text-2xl text-[#FDFBF7] mb-3">Ethnic Silhouettes</h3>
              <p>
                From the sculpted grace of an <strong className="text-[#FDFBF7]">Anarkali</strong> and the celebratory sweep of a
                bridal <strong className="text-[#FDFBF7]">Lehenga</strong> to everyday <strong className="text-[#FDFBF7]">Kurtis</strong>,
                <strong className="text-[#FDFBF7]"> Saree Blouses</strong> and <strong className="text-[#FDFBF7]">Salwar Suits</strong> —
                every ethnic garment is drafted to your exact measurements.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl text-[#FDFBF7] mb-3">Western Silhouettes</h3>
              <p>
                Cocktail <strong className="text-[#FDFBF7]">Gowns</strong>, contemporary
                <strong className="text-[#FDFBF7]"> Dresses</strong>, layered <strong className="text-[#FDFBF7]">Tops</strong> and
                statement <strong className="text-[#FDFBF7]">Skirts</strong> — cut in silk, velvet, georgette, chiffon, cotton or
                linen, with embroidery details of your choosing.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl text-[#FDFBF7] mb-3">Fabric &amp; Embellishment</h3>
              <p>
                Choose from Zari (gold thread), Mirror, Sequin, Stone, Thread work or a Plain finish. Every design is priced live in
                the configurator so you know exactly what your garment will cost.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl text-[#FDFBF7] mb-3">Tailored to Size</h3>
              <p>
                Pick a standard size preset (XS to XXL) or enter your own bust, waist, hip, shoulder and length measurements. The
                AI preview visualises the garment on a model matching your size before a single stitch is cut.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#362B28]/60">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-24 text-center">
          <h2 className="font-display text-4xl lg:text-6xl tracking-tight leading-tight">
            Your design is waiting.<br /><span className="text-[#CFA05E]">Begin in the configurator.</span>
          </h2>
          <div className="mt-10 inline-flex">
            <Link to="/designer" data-testid="cta-start-design">
              <RcButton variant="primary">Open the Configurator <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></RcButton>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
